import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { GeneratedMeta } from "../lib/types";
import { formatDate } from "../lib/format";
import { fetchLlamaStatus } from "../lib/live-api";
import { invalidateAllData, queryKeys } from "../lib/query-client";
import { ActionsPanel } from "./ActionsPanel";

const SOURCE_LABEL: Record<string, string> = {
  github: "live · GitHub",
  assets: "live · deploy",
  static: "static build",
};

export function FreshnessBar({ meta, selectedSkill }: { meta: GeneratedMeta; selectedSkill?: string | null }) {
  const source = meta.dataSource ?? "static";
  const sourceLabel = SOURCE_LABEL[source] ?? source;
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);

  const { data: llama } = useQuery({
    queryKey: queryKeys.llama,
    queryFn: fetchLlamaStatus,
    staleTime: 60_000,
    retry: false,
  });

  async function syncData() {
    setSyncing(true);
    await invalidateAllData();
    await qc.refetchQueries({ queryKey: queryKeys.snapshot });
    setSyncing(false);
  }

  return (
    <div
      className="freshness-bar px-4 py-2 border-b border-rule font-mono text-[10px] uppercase tracking-[0.12em] text-muted flex flex-wrap items-center gap-x-4 gap-y-2"
      role="status"
      aria-live="polite"
    >
      <span>
        Data · {formatDate(meta.generated)}
        {meta.isNewerThanStatic && (
          <span className="text-good ml-2" title="GitHub has newer data than this deploy">
            ↑ newer on main
          </span>
        )}
      </span>
      <span className={source === "github" ? "text-good" : ""}>{sourceLabel}</span>
      {meta.gitSha && <span>sha {meta.gitSha}</span>}
      <span>{meta.skillCount} skills</span>
      <span>avg {meta.avgCombined}%</span>
      {meta.llmAvailable ? (
        <span className="text-good" title={meta.llmProviderCounts ? Object.entries(meta.llmProviderCounts).map(([k, v]) => `${k}:${v}`).join(" · ") : undefined}>
          {meta.withLlm} with LLM
          {meta.llmProviderCounts && Object.keys(meta.llmProviderCounts).length > 0 && (
            <span className="text-muted ml-1">
              ({Object.entries(meta.llmProviderCounts).map(([k, v]) => `${k} ${v}`).join(" · ")})
            </span>
          )}
        </span>
      ) : llama?.ok ? (
        <span className="text-warn" title={`llama.cpp ready — ${llama.modelCount} models`}>
          LLM off · llama ready
        </span>
      ) : (
        <span className="text-warn" title={llama?.error ?? "Run combined-score"}>
          LLM layer off
        </span>
      )}
      <button
        type="button"
        onClick={() => void syncData()}
        disabled={syncing}
        className="border border-fg px-2 py-0.5 hover:bg-[var(--hover)] disabled:opacity-50"
      >
        {syncing ? "Syncing…" : "Sync data"}
      </button>
      <ActionsPanel selectedSkill={selectedSkill} compact />
    </div>
  );
}